import { Box, Button, Flex, Progress, Spinner } from '@chakra-ui/react';
import moment from 'moment';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { Task } from '@app/api/tasks';
import { Dialog, Txt } from '@app/components/common';
import { useAlert, useApi } from '@app/hooks';

import {
  formatProgress,
  formatTaskType,
  isActiveStatus,
  isCancellableStatus,
  TASK_STATUS_COLORS,
  TASK_STATUS_LABELS,
} from '../constants';
import { decodeUtf8Message } from '../utils';
import { TaskLogJournal } from './task-log-journal';

const POLL_INTERVAL_MS = 3000;

const SummaryItem = ({ label, value }: { label: string; value: ReactNode }) => (
  <Flex direction="column" minW="140px">
    <Txt color="gray.400" fontSize="sm">
      {label}
    </Txt>
    <Box>{value}</Box>
  </Flex>
);

const formatDate = (value?: string) => (value ? moment(value).format('DD.MM.YYYY HH:mm:ss') : '—');

type TaskDetailsDialogProps = {
  taskId: number | null;
  onClose: () => void;
  /** Вызывается после изменения задачи (отмена), чтобы обновить таблицу. */
  onChanged?: () => void;
};

/** Детали фоновой задачи: прогресс, журнал постранично, отмена. */
export const TaskDetailsDialog = ({ taskId, onClose, onChanged }: TaskDetailsDialogProps) => {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const { success, error: alertError } = useAlert();

  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const loadTask = useCallback(async (id: number, silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const response = await apiRef.current.tasks.getTask({ id, logs: 0 });
      if (response?.code === 200 && response.data) {
        setTask(response.data);
      } else if (!silent) {
        setTask(null);
      }
    } catch (e) {
      console.error(e);
      if (!silent) {
        setTask(null);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    setTask(null);
    if (taskId == null) {
      return;
    }
    loadTask(taskId);
  }, [taskId, loadTask]);

  // Пока задача активна — подтягиваем прогресс раз в несколько секунд;
  // журнал перечитывает себя сам по refreshKey.
  useEffect(() => {
    if (taskId == null || !isActiveStatus(task?.status)) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      loadTask(taskId, true);
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [taskId, task?.status, loadTask]);

  const onCancel = useCallback(async () => {
    if (taskId == null) {
      return;
    }
    setCanceling(true);
    try {
      const response = await apiRef.current.tasks.cancelTask(taskId);
      if (response?.code === 200) {
        success('Запрос на отмену отправлен');
      }
      await loadTask(taskId, true);
      onChanged?.();
    } catch (e) {
      console.error(e);
      alertError('Не удалось отменить задачу');
    } finally {
      setCanceling(false);
    }
  }, [taskId, loadTask, success, alertError, onChanged]);

  return (
    <Dialog
      open={taskId != null}
      onClose={onClose}
      title={task ? `Задача #${task.id}` : `Задача${taskId != null ? ` #${taskId}` : ''}`}
      width="900px"
      actions={
        <Flex gap={2} justifyContent="flex-end">
          {isCancellableStatus(task?.status) && (
            <Button colorScheme="orange" variant="outline" onClick={onCancel} isLoading={canceling}>
              Отменить
            </Button>
          )}
          <Button onClick={onClose}>Закрыть</Button>
        </Flex>
      }
    >
      {loading && !task && (
        <Flex justifyContent="center" py={8}>
          <Spinner />
        </Flex>
      )}

      {!loading && !task && taskId != null && <Txt color="gray.500">Не удалось загрузить задачу</Txt>}

      {task && (
        <Flex direction="column" gap={4}>
          <Flex gap={4} flexWrap="wrap">
            <SummaryItem label="Тип" value={<Txt>{formatTaskType(task.type)}</Txt>} />
            <SummaryItem
              label="Статус"
              value={
                <Txt color={TASK_STATUS_COLORS[task.status] || 'gray.500'} fontWeight="medium">
                  {TASK_STATUS_LABELS[task.status] || task.status}
                </Txt>
              }
            />
            <SummaryItem label="Автор" value={<Txt>{task.created_by || '—'}</Txt>} />
            <SummaryItem label="Источник" value={<Txt>{task.source || '—'}</Txt>} />
            <SummaryItem label="Создана" value={<Txt>{formatDate(task.created_at)}</Txt>} />
            <SummaryItem label="Запущена" value={<Txt>{formatDate(task.started_at)}</Txt>} />
            <SummaryItem label="Завершена" value={<Txt>{formatDate(task.finished_at)}</Txt>} />
          </Flex>

          <Box>
            <Flex justifyContent="space-between" mb={1}>
              <Txt color="gray.400" fontSize="sm">
                Прогресс
              </Txt>
              <Txt color="gray.500" fontSize="sm">
                {formatProgress(task.progress)}
                {task.total != null ? ` · ${task.processed || 0}/${task.total}` : ''}
                {task.failed ? ` · ошибок: ${task.failed}` : ''}
              </Txt>
            </Flex>
            <Progress
              value={(task.progress || 0) * 100}
              size="sm"
              borderRadius="md"
              colorScheme={task.status === 'failed' ? 'red' : 'blue'}
            />
          </Box>

          {task.error && (
            <Box>
              <Txt color="gray.400" fontSize="sm" mb={1}>
                Ошибка
              </Txt>
              <Txt color="red.500" whiteSpace="pre-wrap">
                {decodeUtf8Message(task.error)}
              </Txt>
            </Box>
          )}

          {task.stats && Object.keys(task.stats).length > 0 && (
            <Box>
              <Txt color="gray.400" fontSize="sm" mb={2}>
                Статистика
              </Txt>
              <Flex gap={4} flexWrap="wrap">
                {Object.entries(task.stats).map(([key, value]) => (
                  <SummaryItem key={key} label={key} value={<Txt>{String(value ?? '—')}</Txt>} />
                ))}
              </Flex>
            </Box>
          )}

          <TaskLogJournal
            key={task.id}
            taskId={task.id}
            refreshKey={`${task.status}|${task.updated_at}|${task.processed}|${task.failed}`}
          />
        </Flex>
      )}
    </Dialog>
  );
};
